import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useInRouterContext } from 'react-router'
import { useQuery } from '@tanstack/react-query'
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { Button } from './button'
import { usePermissions } from 'src/hooks/use-permissions'
import { botTokenQueries } from 'src/services/bot-tokens'
import { useAuth } from 'src/stores/auth'
import { cn } from 'src/utils/style'
import { ASSISTANT_HINT_DISMISSED_PREFIX } from 'src/utils/local-storage'

export const CONNECT_ASSISTANT_HREF = '/organization-settings#connect-assistant'

// Kept across logout (see `clearLocalStorageOnLogout`); keyed per user.
const storageKey = (userId: string, hintId: string) =>
  `${ASSISTANT_HINT_DISMISSED_PREFIX}${userId}.${hintId}`

const readDismissed = (key: string) => {
  try {
    return window.localStorage.getItem(key) === '1'
  } catch {
    return false
  }
}

const writeDismissed = (key: string) => {
  try {
    window.localStorage.setItem(key, '1')
  } catch {
    // Storage unavailable (private mode): dismissal lasts for this render only.
  }
}

/**
 * True when the active org has at least one connected AI assistant. Same source
 * as `ActiveConnections` in `connect-assistant-section.tsx`: connector bots
 * (those with a `connectionClientName`). Only admins can list bots; for
 * everyone else the answer is unknown and treated as "not connected".
 */
const useHasActiveConnection = () => {
  const orgId = useAuth.use.activeOrgId()
  const { hasPermission } = usePermissions()
  const canList = hasPermission('organization.manage_roles')
  const query = useQuery({
    ...botTokenQueries.list({ filters: { organization: orgId ?? undefined } }),
    enabled: Boolean(orgId) && canList,
  })
  const hasConnection = (query.data?.results ?? []).some((bot) => bot.connectionClientName)
  return { hasConnection, isLoading: canList && query.isLoading }
}

interface AssistantHintProps {
  /** Stable id — dismissal is remembered per user and per hint id. */
  id: string
  /** The one-line prompt. Pass an already-translated string. */
  text: string
  className?: string
}

/**
 * One-line prompt recommending an AI assistant for this screen, linking to
 * Settings › Connect assistant. Dismissible; hidden once the org has an active
 * MCP connection. Only use where a deployed MCP tool performs the action.
 */
export const AssistantHint = ({ id, text, className }: AssistantHintProps) => {
  const { t } = useTranslation()
  const userId = useAuth.use.user()?.id ?? 'anonymous'
  const key = storageKey(userId, id)
  const [dismissed, setDismissed] = useState(() => readDismissed(key))
  const { hasConnection, isLoading } = useHasActiveConnection()
  // Some hosts (e.g. modals rendered in isolation) sit outside a router.
  const inRouter = useInRouterContext()

  if (dismissed || hasConnection || isLoading) return null

  return (
    <div
      className={cn([
        'flex items-center gap-2 rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-secondary',
        className,
      ])}
      data-testid={`assistant-hint-${id}`}
    >
      <SparklesIcon className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
      <span className="flex-1">
        {text}{' '}
        {inRouter ? (
          <Link to={CONNECT_ASSISTANT_HREF} className="text-primary hover:underline">
            {t('assistantHint.connect')}
          </Link>
        ) : (
          <a href={CONNECT_ASSISTANT_HREF} className="text-primary hover:underline">
            {t('assistantHint.connect')}
          </a>
        )}
      </span>
      <Button
        variant="ghost"
        size="sm"
        iconOnly
        tooltip={t('assistantHint.dismiss')}
        onClick={() => {
          writeDismissed(key)
          setDismissed(true)
        }}
        data-testid={`assistant-hint-${id}-dismiss`}
      >
        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  )
}
